import { useState } from "react";
import { useTranslation } from "react-i18next";
import { revokeConnector, type ListedConnector } from "../../../api/connectorsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode, toUiError } from "../../../errors/uiError";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { appPaths } from "../../../shared/routing/appPaths";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ButtonLink from "../../../ui/buttons/ButtonLink";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import ConnectorDeviceDetails from "../ConnectorDeviceDetails";
import ConnectorRoutes from "./ConnectorRoutes";
import { GUIDE_STARTS_AT_PAIRING } from "../setup/useConnectorSetup";
import PcRename from "./PcRename";

/** A button a PC line offers about this browser's PC (W-06): "I'm here", "Not this PC". */
export interface PcLineAction {
  readonly label: string;
  readonly onClick: () => void;
}

interface ConnectorPcLineProps {
  connector: ListedConnector;
  /** The PC was renamed or revoked: the list is read again. */
  onChanged: () => void;
  /** Behind Details: correcting which PC this browser is on. */
  thisPcAction?: PcLineAction | undefined;
  /** In the row itself, as when the page asks which PC the person is at. */
  rowAction?: PcLineAction | undefined;
}

/**
 * One paired PC (F49, F78): its name and whether it is connected, or what to do to connect it.
 * Rename, the computer's own name once renamed, system, version, key fingerprint, the routes it
 * reported (Q-93), Revoke and correcting which PC this browser is on wait behind Details.
 */
export default function ConnectorPcLine({ connector, onChanged, thisPcAction, rowAction }: ConnectorPcLineProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const { device } = connector;

  return (
    <li className="app-card-muted space-y-2 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="app-title text-sm font-semibold">{connector.name}</span>
        <PcPresence connector={connector} />
        {/* Where it isn't installed (any more), the installer (F153); the line says how to start it where it is. */}
        {connector.status.online || connector.hostedByServer ? null : (
          <ButtonLink to={appPaths.connectorSetup()} state={GUIDE_STARTS_AT_PAIRING} variant="primary" size="xs">
            {t("connectors.list.getConnector")}
          </ButtonLink>
        )}
        {rowAction === undefined ? null : (
          <AppButton type="button" variant="primary" size="xs" className="ml-auto" onClick={rowAction.onClick}>{rowAction.label}</AppButton>
        )}
        <AppButton type="button" variant="ghost" size="xs" className={rowAction === undefined ? "ml-auto" : undefined} aria-expanded={open} onClick={() => { setOpen((shown) => !shown); }}>
          {open ? t("connectors.list.hideDetails") : t("connectors.list.details")}
        </AppButton>
      </div>
      {open ? (
        <div className="space-y-2">
          {connector.status.online || device.platformOs !== "linux" ? null : (
            <p className="app-text-muted">{t("connectors.list.startService", { pc: connector.name })}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <PcRename connector={connector} onRenamed={onChanged} />
            {thisPcAction === undefined ? null : (
              <AppButton type="button" variant="ghost" size="xs" onClick={thisPcAction.onClick}>{thisPcAction.label}</AppButton>
            )}
          </div>
          <ConnectorDeviceDetails
            device={device}
            showLabel={connector.givenName !== null}
            trailingRows={<DefinitionListItem term={t("connectors.list.pairedAt")}>{formatDateTime(connector.pairedAt, i18n.resolvedLanguage)}</DefinitionListItem>}
          />
          {connector.status.reportReceivedAt === null ? null : <ConnectorRoutes status={connector.status} />}
          {confirmingRevoke ? (
            <RevokeConfirmation connector={connector} onRevoked={onChanged} onCancel={() => { setConfirmingRevoke(false); }} />
          ) : (
            <AppButton type="button" variant="danger" size="xs" onClick={() => { setConfirmingRevoke(true); }}>
              {t("connectors.list.revoke")}
            </AppButton>
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Connected, or when it was last seen. PCs with a tray say to start it from the Start menu: while
 * the connector isn't running there may be no tray icon to click (F153), and a start from the
 * Start menu also resumes a tray whose connector stopped. Linux recovery belongs to the PC line's
 * setup action and folded commands (P-B, F118).
 */
export function PcPresence({ connector }: { connector: ListedConnector }) {
  const { t, i18n } = useTranslation();
  const { status } = connector;
  if (status.online) return <span className="app-text-muted">{t("connectors.list.online")}</span>;
  return (
    <span className="app-text-muted">
      {status.lastSeenAt === null ? null : (
        <time dateTime={status.lastSeenAt} title={formatDateTime(status.lastSeenAt, i18n.resolvedLanguage)}>
          {t("connectors.list.lastSeen", { when: formatTimeAgo(status.lastSeenAt, i18n.resolvedLanguage) })}
          {connector.device.platformOs === "linux" ? null : " · "}
        </time>
      )}
      {connector.device.platformOs === "linux" ? (status.lastSeenAt === null ? t("connectors.list.neverSeen") : null) : t("connectors.list.startOn", { pc: connector.name })}
    </span>
  );
}

/** Revoking asks once more, because a revoked connector cannot be brought back. */
function RevokeConfirmation({ connector, onRevoked, onCancel }: { connector: ListedConnector; onRevoked: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const revoking = state.status === "submitting";

  return (
    <div className="space-y-2" role="group" aria-label={t("connectors.list.revokeConfirmTitle", { label: connector.name })}>
      <p className="app-text text-sm">{t("connectors.list.revokeConfirmMessage", { label: connector.name })}</p>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <AppButton
          type="button"
          variant="dangerSolid"
          size="xs"
          disabled={revoking}
          onClick={() => {
            submit(async () => {
              try {
                await revokeConnector(connector.connectorId);
              } catch (caught) {
                // Revoked meanwhile (another tab, an administrator): the list shows it so.
                if (!isProblemWithCode(toUiError(caught), "illegal_state_transition")) {
                  throw caught;
                }
              }
              onRevoked();
            });
          }}
        >
          {revoking ? t("connectors.list.revoking") : t("connectors.list.revokeConfirm")}
        </AppButton>
        <AppButton type="button" variant="secondary" size="xs" disabled={revoking} onClick={onCancel}>
          {t("common.cancel")}
        </AppButton>
      </div>
    </div>
  );
}
